/**
 * BIGWA - useFocusMode Hook v3.0
 * 
 * Gerencia dois modos de foco independentes:
 * 
 * 1. isFocusMode (tecla F): Apenas recolhe toolbar/SI. Sem fundo verde.
 * 2. isOutlinerFocusMode (botão no Outliner): Fundo verde + recolhe UI.
 * 
 * v3.0 BREAKING CHANGE:
 * - Separação de estados: tecla F não ativa mais o fundo verde
 * - Novo estado isOutlinerFocusMode exclusivo para o Outliner
 * - Botão Focus no Outliner usa toggleOutlinerFocusMode()
 * 
 * v2.2 FIX:
 * - Listener com capture=true para garantir prioridade sobre outros listeners
 * - Evento focus-mode-exit agora inclui flag para colapsar SI
 * - Adicionado log de debug para troubleshooting
 * 
 * v2.1:
 * - Adiciona persist para evitar estado corrompido
 * - Fallback seguro: se persistido por mais de 10min, auto-reset
 * - ESC sempre funciona como saída de emergência
 * 
 * Comportamento:
 * - Sidebar colapsa usando mecanismo nativo do DashboardLayout
 * - SI minimiza (apenas chevron)
 * - Atalho: F para toggle UI, Escape para sair
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/shallow';
import { useEffect } from 'react';
import { useGlobalInspector } from './useGlobalInspector';
import { useGlobalModalStore } from '../stores/globalModalStore';

interface FocusModeState {
  // Estado para tecla F (recolhe UI, sem fundo verde)
  isFocusMode: boolean;
  activatedAt: number | null;
  previousSidebarState: boolean | null;
  
  // 🆕 v3.0: Estado separado para Outliner Focus Mode (fundo verde)
  isOutlinerFocusMode: boolean;
  outlinerFocusActivatedAt: number | null;
  
  // Ações para isFocusMode (tecla F)
  toggleFocusMode: () => void;
  enterFocusMode: () => void;
  exitFocusMode: () => void;
  
  // 🆕 v3.0: Ações para Outliner Focus Mode
  toggleOutlinerFocusMode: () => void;
  enterOutlinerFocusMode: () => void;
  exitOutlinerFocusMode: () => void;
  
  savePreviousSidebarState: (collapsed: boolean) => void;
  getPreviousSidebarState: () => boolean | null;
  _reset: () => void;
}

// Tempo máximo em Focus Mode antes de auto-reset (10 minutos)
const MAX_FOCUS_DURATION_MS = 10 * 60 * 1000;

export const useFocusModeStore = create<FocusModeState>()(
  persist(
    (set, get) => ({
      isFocusMode: false,
      activatedAt: null,
      previousSidebarState: null,
      
      // 🆕 v3.0: Estado inicial do Outliner Focus Mode
      isOutlinerFocusMode: false,
      outlinerFocusActivatedAt: null,
      
      // ============================================================
      // AÇÕES PARA isFocusMode (Tecla F) - SEM fundo verde
      // ============================================================
      
      toggleFocusMode: () => {
        // Lógica inteligente:
        // Se algo está recolhido (sidebar OU SI) → restaura tudo
        // Se tudo está aberto → recolhe tudo
        const siState = useGlobalInspector.getState();
        const sidebarCollapsed = localStorage.getItem('bigwa-sidebar-collapsed') === 'true';
        const siClosed = !siState.isOpen;

        const somethingIsCollapsed = sidebarCollapsed || siClosed || get().isFocusMode;

        if (somethingIsCollapsed) {
          // Restaura tudo
          set({ isFocusMode: false, activatedAt: null });
          window.dispatchEvent(new CustomEvent('bigwa:focus-mode-exit', {
            detail: { shouldCollapseSI: true }
          }));
        } else {
          // Recolhe tudo
          set({ isFocusMode: true, activatedAt: Date.now() });
          window.dispatchEvent(new CustomEvent('bigwa:focus-mode-enter'));
        }
      },
      
      enterFocusMode: () => {
        set({ isFocusMode: true, activatedAt: Date.now() });
        window.dispatchEvent(new CustomEvent('bigwa:focus-mode-enter'));
      },
      
      exitFocusMode: () => {
        set({ isFocusMode: false, activatedAt: null });
        window.dispatchEvent(new CustomEvent('bigwa:focus-mode-exit', { 
          detail: { shouldCollapseSI: true } 
        }));
      },
      
      // ============================================================
      // 🆕 v3.0: AÇÕES PARA Outliner Focus Mode - COM fundo verde
      // ============================================================
      
      toggleOutlinerFocusMode: () => {
        const current = get().isOutlinerFocusMode;
        if (current) {
          set({ isOutlinerFocusMode: false, outlinerFocusActivatedAt: null });
        } else {
          // Ativa fundo verde E colapsa UI
          set({ 
            isOutlinerFocusMode: true, 
            outlinerFocusActivatedAt: Date.now(),
            // Também ativa isFocusMode para colapsar UI
            isFocusMode: true,
            activatedAt: Date.now()
          });
          window.dispatchEvent(new CustomEvent('bigwa:focus-mode-enter'));
        }
      },

      enterOutlinerFocusMode: () => {
        set({
          isOutlinerFocusMode: true,
          outlinerFocusActivatedAt: Date.now(),
          isFocusMode: true,
          activatedAt: Date.now()
        });
        window.dispatchEvent(new CustomEvent('bigwa:focus-mode-enter'));
      },
      
      exitOutlinerFocusMode: () => {
        set({ 
          isOutlinerFocusMode: false, 
          outlinerFocusActivatedAt: null,
          isFocusMode: false,
          activatedAt: null
        });
        window.dispatchEvent(new CustomEvent('bigwa:focus-mode-exit', {
          detail: { shouldCollapseSI: true } 
        }));
      },
      
      // ============================================================
      // HELPERS
      // ============================================================
      
      savePreviousSidebarState: (collapsed: boolean) => {
        set({ previousSidebarState: collapsed });
      },
      
      getPreviousSidebarState: () => get().previousSidebarState,
      
      _reset: () => {
        set({ 
          isFocusMode: false, 
          activatedAt: null, 
          previousSidebarState: null,
          isOutlinerFocusMode: false,
          outlinerFocusActivatedAt: null
        });
      },
    }),
    {
      name: 'bigwa-focus-mode',
      // Ao carregar do storage, verifica se está em Focus Mode há muito tempo
      onRehydrateStorage: () => (state) => {
        if (state?.isFocusMode && state?.activatedAt) {
          const elapsed = Date.now() - state.activatedAt;
          if (elapsed > MAX_FOCUS_DURATION_MS) {
            setTimeout(() => {
              useFocusModeStore.getState()._reset();
            }, 0);
          }
        }
        // Também verifica Outliner Focus Mode
        if (state?.isOutlinerFocusMode && state?.outlinerFocusActivatedAt) {
          const elapsed = Date.now() - state.outlinerFocusActivatedAt;
          if (elapsed > MAX_FOCUS_DURATION_MS) {
            setTimeout(() => {
              useFocusModeStore.getState()._reset();
            }, 0);
          }
        }
      },
    }
  )
);

/**
 * Hook para usar o Modo Foco com atalho de teclado
 * @param enableShortcut - Se true, ativa o atalho F (default: true)
 */
export function useFocusMode(enableShortcut = true) {
  const {
    isFocusMode,
    toggleFocusMode,
    enterFocusMode,
    exitFocusMode,
    // 🆕 v3.0: Outliner Focus Mode
    isOutlinerFocusMode,
    toggleOutlinerFocusMode,
    enterOutlinerFocusMode,
    exitOutlinerFocusMode,
    savePreviousSidebarState,
    getPreviousSidebarState,
    _reset
  } = useFocusModeStore(
    useShallow(s => ({
      isFocusMode: s.isFocusMode,
      toggleFocusMode: s.toggleFocusMode,
      enterFocusMode: s.enterFocusMode,
      exitFocusMode: s.exitFocusMode,
      isOutlinerFocusMode: s.isOutlinerFocusMode,
      toggleOutlinerFocusMode: s.toggleOutlinerFocusMode,
      enterOutlinerFocusMode: s.enterOutlinerFocusMode,
      exitOutlinerFocusMode: s.exitOutlinerFocusMode,
      savePreviousSidebarState: s.savePreviousSidebarState,
      getPreviousSidebarState: s.getPreviousSidebarState,
      _reset: s._reset,
    }))
  );
  
  // Auto-exit se ficar muito tempo (safety net durante uso)
  useEffect(() => {
    if (!isFocusMode && !isOutlinerFocusMode) return;
    
    const checkDuration = setInterval(() => {
      const state = useFocusModeStore.getState();
      
      // Verifica isFocusMode
      if (state.isFocusMode && state.activatedAt) {
        const elapsed = Date.now() - state.activatedAt;
        if (elapsed > MAX_FOCUS_DURATION_MS) {
          exitFocusMode();
        }
      }
      
      // Verifica Outliner Focus Mode
      if (state.isOutlinerFocusMode && state.outlinerFocusActivatedAt) {
        const elapsed = Date.now() - state.outlinerFocusActivatedAt;
        if (elapsed > MAX_FOCUS_DURATION_MS) {
          exitOutlinerFocusMode();
        }
      }
    }, 60000); // Verifica a cada minuto
    
    return () => clearInterval(checkDuration);
  }, [isFocusMode, isOutlinerFocusMode, exitFocusMode, exitOutlinerFocusMode]);
  
  useEffect(() => {
    if (!enableShortcut) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignora se estiver em input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.isContentEditable
      ) {
        return;
      }
      
      // F para toggle UI (SEM fundo verde)
      // Ignora se modal/janela flutuante está ativa — SI está oculto por design
      if (e.key === 'f' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        const modalActive = useGlobalModalStore.getState().activeGlobalModal !== null;
        if (modalActive) return; // F não faz nada quando modal está aberto
        e.preventDefault();
        e.stopPropagation();
        toggleFocusMode();
      }
      
      // Escape SEMPRE sai de ambos os modos
      if (e.key === 'Escape') {
        const state = useFocusModeStore.getState();
        if (state.isOutlinerFocusMode) {
          e.preventDefault();
          e.stopPropagation();
          exitOutlinerFocusMode();
        } else if (state.isFocusMode) {
          e.preventDefault();
          e.stopPropagation();
          exitFocusMode();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [enableShortcut, toggleFocusMode, exitFocusMode, exitOutlinerFocusMode]);
  
  return {
    // Estado UI (tecla F)
    isFocusMode,
    toggleFocusMode,
    enterFocusMode,
    exitFocusMode,
    // 🆕 v3.0: Estado Outliner (fundo verde)
    isOutlinerFocusMode,
    toggleOutlinerFocusMode,
    enterOutlinerFocusMode,
    exitOutlinerFocusMode,
    // Helpers
    savePreviousSidebarState,
    getPreviousSidebarState,
  };
}

export default useFocusMode;
